import { z } from 'zod';
import { ALL_LAYOUT_PRESETS, type LayoutPreset, type TransitionKind } from './edl';

/**
 * Serializable edit commands — the one mutation surface shared by the human UI
 * and the AI agent. The Zod schemas here are the SINGLE source of truth: they
 * validate human input AND are exposed verbatim to the LLM as tool/structured
 * output schemas (Phase 2). There is no second command definition anywhere.
 */

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'expected #RRGGBB hex color');

export const assetRefSchema = z
  .object({
    assetId: z.string().min(1),
    sourceInSec: z.number().nonnegative(),
    sourceOutSec: z.number().positive(),
  })
  .refine((s) => s.sourceOutSec > s.sourceInSec, {
    message: 'sourceOutSec must be greater than sourceInSec',
    path: ['sourceOutSec'],
  });

export const cropRectSchema = z
  .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().min(0).max(1),
    h: z.number().min(0).max(1),
  })
  .refine((c) => c.w > 0 && c.h > 0, {
    message: 'crop width and height must be positive',
  })
  .refine((c) => c.x + c.w <= 1 + 1e-9 && c.y + c.h <= 1 + 1e-9, {
    message: 'crop rect must stay within the source frame (x+w<=1, y+h<=1)',
  });

export const captionStyleSchema = z.object({
  fontFamily: z.string().min(1),
  fontSizePx: z.number().positive(),
  primaryColor: hexColor,
  outlineColor: hexColor,
  outlineWidth: z.number().nonnegative(),
  bold: z.boolean(),
  alignment: z.enum(['top', 'center', 'bottom']),
  marginVPx: z.number().nonnegative(),
});

const transitionKind: z.ZodType<TransitionKind> = z.enum(['cut', 'fade', 'whip', 'zoom']);
const clipFit = z.enum(['blur', 'cover', 'contain']);
/*
 * 🔴 열거를 «손으로» 적지 않는다. 예전에는 `z.enum(['full','basic','expanded'])` 였고,
 *    프리셋을 더하면 그 값을 가진 EDL 이 **조용히 거부된다**(오류가 아니라 검증 실패라
 *    「왜 안 들어가나」가 안 보인다). 목록에서 유도하면 그 경로가 구조적으로 닫힌다.
 */
const layoutPreset: z.ZodType<LayoutPreset> = z.enum(ALL_LAYOUT_PRESETS);

const frame = z.number().int().nonnegative();
const positiveFrames = z.number().int().positive();

export const editCommandSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('addClip'),
    trackId: z.string().min(1),
    source: assetRefSchema,
    at: frame,
    crop: cropRectSchema.optional(),
    fit: clipFit.optional(),
    volume: z.number().min(0).max(1).optional(),
    rect: cropRectSchema.optional(),
  }),
  z.object({
    op: z.literal('split'),
    itemId: z.string().min(1),
    atFrame: positiveFrames,
  }),
  z.object({
    op: z.literal('trim'),
    itemId: z.string().min(1),
    sourceInSec: z.number().nonnegative().optional(),
    sourceOutSec: z.number().positive().optional(),
    durationInFrames: positiveFrames.optional(),
  }),
  z.object({
    op: z.literal('move'),
    itemId: z.string().min(1),
    toTrackId: z.string().min(1),
    at: frame,
  }),
  z.object({
    op: z.literal('remove'),
    itemId: z.string().min(1),
  }),
  z.object({
    op: z.literal('addTransition'),
    afterItemId: z.string().min(1),
    beforeItemId: z.string().min(1),
    kind: transitionKind,
    durationInFrames: positiveFrames,
  }),
  z.object({
    op: z.literal('addCaption'),
    trackId: z.string().min(1),
    text: z.string().min(1),
    from: frame,
    durationInFrames: positiveFrames,
    style: captionStyleSchema.partial().optional(),
  }),
  z.object({
    op: z.literal('editCaption'),
    itemId: z.string().min(1),
    text: z.string().min(1).optional(),
    durationInFrames: positiveFrames.optional(),
    style: captionStyleSchema.partial().optional(),
  }),
  z.object({
    op: z.literal('setCrop'),
    itemId: z.string().min(1),
    crop: cropRectSchema,
  }),
  z.object({
    op: z.literal('setFit'),
    itemId: z.string().min(1),
    fit: clipFit,
  }),
  z.object({
    op: z.literal('addGif'),
    trackId: z.string().min(1),
    at: frame,
    durationInFrames: positiveFrames,
    rect: cropRectSchema,
    // Exactly one of these is set: `src` (resolved URL, from the human UI) or
    // `query` (search keywords, from the AI agent — resolved to `src` before the
    // reducer runs). The reducer requires a concrete `src`. Kept as a plain
    // object (no .refine) so it stays a valid discriminatedUnion member.
    src: z.string().url().optional(),
    query: z.string().min(1).max(120).optional(),
  }),
  z.object({
    op: z.literal('addSfx'),
    trackId: z.string().min(1),
    sfxId: z.string().min(1),
    at: frame,
    durationInFrames: positiveFrames.optional(),
    volume: z.number().min(0).max(1).optional(),
  }),
  z.object({
    op: z.literal('setLayout'),
    layout: layoutPreset,
  }),
  z.object({
    op: z.literal('setTitle'),
    // Both optional: set/replace the title text and/or override style fields.
    // Creating a title (none present) requires `text`; the reducer enforces this.
    text: z.string().min(1).max(200).optional(),
    style: captionStyleSchema.partial().optional(),
  }),
]);

export type EditCommand = z.infer<typeof editCommandSchema>;

/** Narrow helper: extract a single command variant by its `op`. */
export type CommandOf<Op extends EditCommand['op']> = Extract<EditCommand, { op: Op }>;
